<template>
  <div class="upload-status-container" v-if="uploads.length > 0">
    <div class="upload-status-header">
      <div class="upload-status-title">
        <fa icon="fa-cloud-upload-alt" />
        <span>Uploads em andamento</span>
      </div>
      <div class="upload-status-actions">
        <button class="minimize-btn" @click="toggleMinimize">
          <fa :icon="isMinimized ? 'fa-plus' : 'fa-minus'" />
        </button>
      </div>
    </div>
    <div class="upload-status-content" v-if="!isMinimized">
      <div
        class="upload-item"
        v-for="upload in uploads"
        :key="upload.id"
      >
        <div class="upload-info">
          <div class="upload-filename">
            <fa icon="fa-video" />
            <span>{{ upload.filename }}</span>
          </div>
          <div class="upload-details">
            <span class="upload-size">{{ formatFileSize(upload.fileSize) }}</span>
            <span class="upload-speed" v-if="upload.speed > 0">
              {{ formatSpeed(upload.speed) }}
            </span>
          </div>
        </div>
        <div class="upload-progress">
          <div class="progress-bar">
            <div
              class="progress-fill"
              :class="{ uploading: upload.isUploading, completed: upload.isCompleted }"
              :style="{ width: upload.progress + '%' }"
            ></div>
          </div>
          <div class="progress-text">
            <span class="progress-percentage">{{ Math.round(upload.progress) }}%</span>
            <span class="upload-status">
              {{ getStatusText(upload) }}
            </span>
          </div>
        </div>
        <div class="upload-actions">
          <button
            v-if="upload.isUploading"
            class="cancel-btn"
            @click="cancelUpload(upload.id)"
            title="Cancelar upload"
          >
            <fa icon="fa-times" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UploadStatusContainer',
  props: {
    uploads: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isMinimized: false
    };
  },
  methods: {
    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    },
    formatSpeed(bytesPerSecond) {
      if (bytesPerSecond === 0) return '0 B/s';
      const k = 1024;
      const sizes = ['B/s', 'KB/s', 'MB/s'];
      const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
      return Math.round((bytesPerSecond / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    },
    getStatusText(upload) {
      if (upload.isCompleted) {
        return 'Concluído';
      } else if (upload.error) {
        return 'Erro';
      } else if (upload.isUploading) {
        return 'Enviando...';
      }
      return 'Aguardando...';
    },
    cancelUpload(uploadId) {
      this.$emit('cancel-upload', uploadId);
    }
  }
};
</script>

<style scoped>
.upload-status-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 400px;
  max-width: calc(100vw - 40px);
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

.upload-status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.upload-status-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.upload-status-title fa {
  color: #150065;
}

.upload-status-actions {
  display: flex;
  gap: 8px;
}

.minimize-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  color: #666;
  font-size: 14px;
  transition: color 0.2s;
}

.minimize-btn:hover {
  color: #150065;
}

.upload-status-content {
  max-height: 400px;
  overflow-y: auto;
  padding: 12px;
}

.upload-item {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.upload-item:last-child {
  border-bottom: none;
}

.upload-info {
  margin-bottom: 8px;
}

.upload-filename {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.upload-filename fa {
  color: #150065;
  font-size: 14px;
}

.upload-filename span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.upload-details {
  display: flex;
  gap: 12px;
  font-size: 11px;
  color: #666;
  margin-top: 4px;
}

.upload-size,
.upload-speed {
  display: inline-block;
}

.upload-progress {
  margin-top: 8px;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 6px;
}

.progress-fill {
  height: 100%;
  background: #150065;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-fill.uploading {
  background: linear-gradient(90deg, #150065 0%, #250891 100%);
  animation: pulse 1.5s ease-in-out infinite;
}

.progress-fill.completed {
  background: #28a745;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.progress-text {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #666;
}

.progress-percentage {
  font-weight: 600;
  color: #150065;
}

.upload-status {
  color: #666;
}

.upload-actions {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
}

.cancel-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  color: #dc3545;
  font-size: 12px;
  transition: color 0.2s;
}

.cancel-btn:hover {
  color: #c82333;
}

/* Scrollbar styling */
.upload-status-content::-webkit-scrollbar {
  width: 6px;
}

.upload-status-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.upload-status-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.upload-status-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Responsive */
@media (max-width: 480px) {
  .upload-status-container {
    width: calc(100vw - 20px);
    right: 10px;
    bottom: 10px;
  }
}
</style>

